import * as React from 'react';
import {
  Box,
  Button,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import useSWR from 'swr';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import ChipSelect from '../ChipSelect';
import type IRejectWorkCheckInterfaces from './RejectWorkCheckInterfaces';

interface RejectWorkDialogProps {
  barcode: string;
  onClose: () => void;
  showModal: boolean;
  rejectWork: (rejectedWorks: string[]) => void;
  station: string;
  setRejectComment?: React.Dispatch<React.SetStateAction<string>>;
}

const RejectWorkDialog: React.FC<RejectWorkDialogProps> = ({
  showModal, onClose, rejectWork, barcode, station, setRejectComment,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedReasons, setSelectedReasons] = React.useState<string[]>([]);

  const handleClose = () => {
    setSelectedReasons([]);
    onClose();
  };

  const handleRejectWork = () => {
    if (selectedReasons.length === 0) {
      enqueueSnackbar('Please select a reject reason.', { persist: false, variant: 'error' });
    } else {
      rejectWork(selectedReasons);
      handleClose();
    }
  };

  const handleRejectReasonSelect = (reasonChanging: string) => {
    if (selectedReasons.findIndex((r) => r === reasonChanging) === -1) {
      setSelectedReasons([...selectedReasons, reasonChanging]);
    } else {
      const newSelectedReasons = selectedReasons;
      newSelectedReasons.splice(newSelectedReasons.indexOf(reasonChanging), 1);
      setSelectedReasons([...newSelectedReasons]);
    }
  };

  const fetcher = async (url: string, barcodeParam: string) => {
    const res = await axios.get<IRejectWorkCheckInterfaces>(url, {
      params: {
        barcode: barcodeParam,
      },
    })
      .then((response) => response.data);
    return res;
  };

  const rejectWorkReasonUrl = `/${station}/getRejectWorkChecks`;

  const { data } = useSWR<IRejectWorkCheckInterfaces>(barcode
    ? [rejectWorkReasonUrl, barcode]
    : null, (url, barcodeParameter) => fetcher(url, barcodeParameter));

  return (
    <Dialog
      onClose={handleClose}
      open={showModal}
      fullWidth
      maxWidth="md"
    >
      {data
        && (
          <>
            <DialogTitle>
              <Typography variant="overline">
                {'WORK NOT APPROVED'}
              </Typography>
              <Typography variant="h5" id="dialog-header">{'Choose the reason(s) why the work is rejected.'}</Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container>
                {data.reasons?.map((rr) => (
                  <Box m={1}>
                    <ChipSelect
                      key={rr?.reason}
                      label={rr?.reason || ''}
                      selected={rr?.inUse || selectedReasons.findIndex((r) => r === rr?.reason) !== -1}
                      handleClick={() => handleRejectReasonSelect(rr?.reason || '')}
                      disabled={rr?.inUse}
                    />
                  </Box>
                ))}
              </Grid>
              {
                station !== 'Watches' && (
                  <Box my={2}>
                    <Typography variant="body2" color="textSecondary">
                      {'After confirming the reason(s) the work is rejected, bring the repair item to the station specified.'}
                    </Typography>
                  </Box>
                )
              }
              {
                station === 'Watches' && (
                  <Grid>
                    <Grid xs={6}>
                      <Box p={2} pt={2} pb={1}>
                        <Typography variant="body2" color="textSecondary">
                          {'Please provide more information.'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid xs={12}>
                      <Box p={2}>
                        <TextField
                          id="reject-work-comment-input"
                          label="Comment"
                          variant="outlined"
                          type="string"
                          fullWidth
                          onChange={(event) => {
                            const inputVal = event.target.value;
                            const rejectComment: string = inputVal;
                            if (rejectComment) {
                              setRejectComment?.(rejectComment);
                            } else {
                              setRejectComment?.('');
                            }
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )
              }
            </DialogContent>

            <DialogActions>
              <Button color="primary" variant="text" onClick={handleClose}>{'CANCEL'}</Button>
              <Button color="secondary" variant="contained" onClick={() => { handleRejectWork(); }}>{'REJECT WORK'}</Button>
            </DialogActions>
          </>
        )}
    </Dialog>
  );
};

export default RejectWorkDialog;
